class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const updateEvent = FacetFiltersForm.startUpdateEvent(searchParams);
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const countContainerCustomSearch = document.getElementById('custom-search-count');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }
    if (countContainerCustomSearch) {
      countContainerCustomSearch.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, updateEvent)
        : FacetFiltersForm.renderSectionFromFetch(url, event, updateEvent);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static startUpdateEvent(searchParams) {
    const { SearchUpdateEvent, CollectionUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent && !CollectionUpdateEvent) return;

    const facetsForm = document.querySelector('facet-filters-form');
    const facetsContainer = document.querySelector('.facets-container');
    const collectionId = facetsContainer?.dataset.collectionId || null;
    const collectionHandle = facetsContainer?.dataset.collectionHandle || '';
    const currentCount = parseInt(document.getElementById('ProductCount')?.dataset.productCount) || 0;
    const urlSearchParams = new URLSearchParams(searchParams);
    const isSearchPage = facetsContainer?.dataset.template === 'search';
    const isCollectionPage = facetsContainer?.dataset.template === 'collection';
    const dispatchTarget = facetsForm || document;

    let deferred;

    if (isSearchPage) {
      deferred = SearchUpdateEvent.createPromise();
      dispatchTarget.dispatchEvent(
        new SearchUpdateEvent({
          search: {
            query: urlSearchParams.get('q') || '',
            productFilters: SearchUpdateEvent.parseProductFilters(urlSearchParams),
            sortKey: SearchUpdateEvent.getSortKey(urlSearchParams),
          },
          promise: deferred.promise,
        }),
      );
    } else if (isCollectionPage) {
      // collectionId is null for auto-generated collections (/collections/all,
      // /collections/vendors/<x>, etc.) — the SSE schema accepts that.
      deferred = CollectionUpdateEvent.createPromise();
      dispatchTarget.dispatchEvent(
        new CollectionUpdateEvent({
          collection: { id: collectionId, handle: collectionHandle, productsCount: currentCount },
          productFilters: CollectionUpdateEvent.parseProductFilters(urlSearchParams),
          sortKey: CollectionUpdateEvent.getSortKey(urlSearchParams),
          promise: deferred.promise,
        }),
      );
    }

    if (!deferred) return;

    return {
      resolve: (filteredCount) => {
        deferred.resolve({ [isSearchPage ? 'totalCount' : 'productsCount']: filteredCount });
      },
      reject: deferred.reject,
    };
  }

  static renderSectionFromFetch(url, event, updateEvent) {
    fetch(url)
      .then((response) => response.text())
      .then((html) => {
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderSection(html, event, updateEvent);
      })
      .catch((error) => {
        console.error(error);
        updateEvent?.reject(error);
      });
  }

  static renderSectionFromCache(filterDataUrl, event, updateEvent) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderSection(html, event, updateEvent);
  }

  static renderSection(html, event, updateEvent) {
    FacetFiltersForm.renderProductTypeWrapper(html);
    try {
      FacetFiltersForm.renderFilters(html, event);
    } catch (error) {
      // A filter that appears/disappears must never leave the grid stuck in "loading"
      console.error(error);
    }
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html, updateEvent);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
    FacetFiltersForm.syncAllFacetCheckboxes();

    document.querySelectorAll('.js-filter details').forEach(function(details) {
      resetFilterSearch(details);
    });
  }

  // The product-type tab only renders when it has 2+ values, so it can vanish and come
  // back between filter changes. Re-render its wrapper whenever that happens.
  static renderProductTypeWrapper(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    document.querySelectorAll('[data-custom-product-type-wrapper]').forEach((wrapper) => {
      const source = parsedHTML.getElementById(wrapper.id);
      if (!source) return;
      const hadForm = Boolean(wrapper.querySelector('#CustomFacetFiltersForm'));
      const hasForm = Boolean(source.querySelector('#CustomFacetFiltersForm'));
      if (hadForm !== hasForm) wrapper.innerHTML = source.innerHTML;
    });
  }

  // Puts a filter that came back (wasn't in the DOM) in the right place without throwing.
  static insertFacet(elementToRender, facetsToRender, index) {
    const formId = (element) => (element.closest('form') ? element.closest('form').id : '');
    const sameType = (element) =>
      element.className === elementToRender.className && formId(element) === formId(elementToRender);

    // after the closest previous filter of the same type that is on the page
    for (let i = index - 1; i >= 0; i--) {
      const previous = facetsToRender[i];
      if (!sameType(previous)) break;
      const previousInDom = document.getElementById(previous.id);
      if (previousInDom) {
        previousInDom.after(elementToRender);
        return true;
      }
    }

    // before the closest next filter of the same type that is on the page
    for (let i = index + 1; i < facetsToRender.length; i++) {
      const next = facetsToRender[i];
      if (!sameType(next)) break;
      const nextInDom = document.getElementById(next.id);
      if (nextInDom) {
        nextInDom.before(elementToRender);
        return true;
      }
    }

    // inside the same parent (by id) as in the fresh HTML
    const parentId = elementToRender.parentElement ? elementToRender.parentElement.id : '';
    const parentInDom = parentId ? document.getElementById(parentId) : null;
    if (parentInDom) {
      const firstFilter = parentInDom.querySelector('.js-filter');
      firstFilter ? firstFilter.before(elementToRender) : parentInDom.appendChild(elementToRender);
      return true;
    }

    return false;
  }

  static syncAllFacetCheckboxes() {
    document
      .querySelectorAll('#CustomFacetFiltersForm input[type="checkbox"]')
      .forEach((input) => {

        const selector = `#FacetFiltersForm input[name="${CSS.escape(input.name)}"][value="${CSS.escape(input.value)}"]`;
        const native = document.querySelector(selector);

        if (native) {
          native.checked = input.checked;
        }

        // Sync active class
        const tag = input.closest(".tag");

        if (tag) {
          if (input.checked) {
            tag.classList.add("tag--active", "active");
          } else {
            tag.classList.remove("tag--active", "active");
          }
        }
      });
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });

      // Reinitialize infinite scroll
      if (window.initInfiniteScroll) {
        requestAnimationFrame(() => {
          window.initInfiniteScroll();
        });
      }
  }

  static renderProductCount(html, updateEvent) {
    const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
    const sourceCount = parsedHtml.getElementById('ProductCount');
    const count = sourceCount.innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    const countContainerCustomSearch = document.getElementById('custom-search-count');
    container.innerHTML = count;
    container.dataset.productCount = sourceCount.dataset.productCount || '';
    container.dataset.totalCount = sourceCount.dataset.totalCount || '';
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    if (countContainerCustomSearch) {
      countContainerCustomSearch.innerHTML = count;
      countContainerCustomSearch.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));

    updateEvent?.resolve(parseInt(sourceCount.dataset.productCount) || 0);
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#CustomFacetFiltersForm .js-filter, #FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#CustomFacetFiltersForm .js-filter, #FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        // Filter came back after being hidden (e.g. 2nd brand selected) – insert it safely
        FacetFiltersForm.insertFacet(elementToRender, facetsToRender, index);
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);

        // custom filters //
        // Text/number inputs and our price range slider handles need the same
        // focus-retention treatment Dawn gives text inputs, otherwise focus is
        // lost after every debounced AJAX re-render — annoying while typing a
        // price, and breaks keyboard (arrow-key) use of the slider handles.
        const isTextInput = ['text', 'number', 'range'].includes(event.target.getAttribute('type'));
        // custom filters //
        
        // const isTextInput = event.target.getAttribute('type') === 'text';

        if (!isTextInput) {
          // Try to return focus to the same checkbox the user just toggled,
          // re-selecting it from the freshly rendered HTML by its id.
          const originatingInputId = event.target.id;
          const matchingInput = originatingInputId
            ? newFacetDetailsElement.querySelector(`#${CSS.escape(originatingInputId)}`)
            : null;

          if (matchingInput) {
            matchingInput.focus();
          } else {
            // Fallback to summary/close button if the checkbox can't be found
            const fallbackSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
              ? `.mobile-facets__close-button`
              : `.facets__summary`;
            const fallbackElement = newFacetDetailsElement.querySelector(fallbackSelector);
            if (fallbackElement) fallbackElement.focus();
          }
        }
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);

    Array.from(formData.keys()).forEach((key) => {
      if (formData.get(key) === '') {
        formData.delete(key);
      }
    });

    const minInput = form.querySelector('[name="filter.v.price.gte"]');
    const maxInput = form.querySelector('[name="filter.v.price.lte"]');

    if (minInput && maxInput) {
      const defaultMin = minInput.min || '0';
      const defaultMax = maxInput.max;

      if (
        minInput.value === defaultMin &&
        maxInput.value === defaultMax
      ) {
        formData.delete('filter.v.price.gte');
        formData.delete('filter.v.price.lte');
      }
    }

    return new URLSearchParams(formData).toString();
  }

  // custom filters //
    syncFacetCheckboxes(changedInput) {
      const selector = `input[name="${CSS.escape(changedInput.name)}"][value="${CSS.escape(changedInput.value)}"]`;

      document.querySelectorAll(selector).forEach((input) => {
        if (input !== changedInput) {
          input.checked = changedInput.checked;
        }
      });
    }
  // custom filters //

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();

    // custom filters //

      // Ignore filter search box
      if (event.target.closest('.filter-search-input')) {
        return;
      }

      if (event.target.matches('input[type="checkbox"]')) {
        this.syncFacetCheckboxes(event.target);
      }

      if (event.target.closest('#CustomFacetFiltersForm')) {
        const nativeDetails = document.querySelector('#FacetFiltersForm details[id^="Details-"]');

        if (nativeDetails) {
          nativeDetails.open = true;
        }
      }
    // custom filters //

    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'CustomFacetFiltersForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
      setTimeout(() => {
        if (window.initInfiniteScroll) {
            window.initInfiniteScroll();
        }
      },100);
        
    }

  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

/**
 * <price-range> — custom dual-handle Price slider.
 *
 * This element is the wrapper already rendered by snippets/facets.liquid:
 *   <price-range class="facets__price">
 *     {% render 'price-facet', ... %}    <!-- boxed slider markup -->
 *   </price-range>
 *
 * The two <input type="range"> inputs rendered inside it (see
 * snippets/price-facet.liquid) carry Shopify's native price filter params
 * directly as their `name`:
 *   - filter.v.price.gte (Min handle)
 *   - filter.v.price.lte (Max handle)
 *
 * Because those inputs live inside the standard #FacetFiltersForm /
 * #FacetFiltersFormMobile markup, they're automatically picked up by
 * FacetFiltersForm's existing `input` listener (see the constructor of
 * FacetFiltersForm above) and debounced-submitted through the same AJAX
 * pipeline as every other facet — no separate submit logic is needed here.
 * This element is only responsible for:
 *   1. Drawing the filled segment of the track between the two handles.
 *   2. Keeping the two live "₹X — ₹Y" labels in sync as the shopper drags.
 *   3. Preventing the handles from crossing (Min can never exceed Max).
 *   4. Keeping whichever handle is closer to the shopper's cursor on top,
 *      since the two range inputs are stacked exactly on top of one another.
 */

class PriceRange extends HTMLElement {
  constructor() {
    super();

    this.minInput = this.querySelector('[data-price-min-input]');
    this.maxInput = this.querySelector('[data-price-max-input]');
    this.trackFill = this.querySelector('[data-price-track-fill]');
    this.minLabel = this.querySelector('[data-price-min-label]');
    this.maxLabel = this.querySelector('[data-price-max-label]');
    const box = this.querySelector('.price-range__box');

    // Snippet wasn't rendered with the expected slider markup — bail out safely.
    if (!this.minInput || !this.maxInput || !box) return;

    this.rangeMin = Number(box.dataset.priceRangeMin) || 0;
    this.rangeMax = Number(box.dataset.priceRangeMax) || 0;
    this.moneyFormat = box.dataset.moneyFormat || '₹{{amount_no_decimals}}';

    // Live UI feedback on every tick of the drag — cheap, so no debounce needed.
    this.minInput.addEventListener('input', this.onMinInput.bind(this));
    this.maxInput.addEventListener('input', this.onMaxInput.bind(this));

    // The actual AJAX filter update is handled elsewhere (FacetFiltersForm's
    // debounced 'input' listener on the whole form) — nothing to wire up here.

    this.render();
  }

  onMinInput() {
    // Never let Min pass Max.
    if (Number(this.minInput.value) > Number(this.maxInput.value)) {
      this.minInput.value = this.maxInput.value;
    }
    this.raiseStackingOrder(this.minInput, this.maxInput);
    this.render();
  }

  onMaxInput() {
    // Never let Max go below Min.
    if (Number(this.maxInput.value) < Number(this.minInput.value)) {
      this.maxInput.value = this.minInput.value;
    }
    this.raiseStackingOrder(this.maxInput, this.minInput);
    this.render();
  }

  // The two range inputs are visually stacked on top of each other. Whichever
  // handle the shopper is actively dragging needs the higher z-index so it
  // stays grabbable instead of the other (invisible, non-hovered) thumb
  // intercepting the pointer.
  raiseStackingOrder(activeInput, otherInput) {
    activeInput.style.zIndex = '2';
    otherInput.style.zIndex = '1';
  }

  // Redraws the filled track segment and the two currency labels from the
  // inputs' current values. Called on every 'input' tick during a drag.
  render() {
    const min = Number(this.minInput.value);
    const max = Number(this.maxInput.value);
    const span = this.rangeMax - this.rangeMin || 1;

    if (this.trackFill) {
      const leftPercent = ((min - this.rangeMin) / span) * 100;
      const rightPercent = 100 - ((max - this.rangeMin) / span) * 100;
      this.trackFill.style.left = `${leftPercent}%`;
      this.trackFill.style.right = `${rightPercent}%`;
    }

    if (this.minLabel) this.minLabel.textContent = this.formatMoney(min);
    if (this.maxLabel) this.maxLabel.textContent = this.formatMoney(max);
  }

  // Minimal stand-in for Shopify's Currency.formatMoney, driven by the
  // shop's own money_format setting (passed in via a data attribute) so the
  // live labels match the currency symbol/format shown everywhere else on
  // the storefront without needing a full money.js include.
  formatMoney(cents) {
    const placeholderMatch = this.moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/);
    const placeholder = placeholderMatch ? placeholderMatch[1] : 'amount_no_decimals';
    const noDecimals = placeholder.includes('no_decimals');
    const withComma = placeholder.includes('with_comma_separator');

    let value = Number(cents).toFixed(noDecimals ? 0 : 2);
    let [whole, decimal] = value.split('.');
    whole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    value = decimal ? `${whole}${withComma ? ',' : '.'}${decimal}` : whole;

    return this.moneyFormat.replace(/\{\{\s*\w+\s*\}\}/, value).replace(/<[^>]*>/g, '');
  }
}
customElements.define('price-range', PriceRange);

// class PriceRange extends HTMLElement {
//   constructor() {
//     super();
//     this.querySelectorAll('input').forEach((element) => {
//       element.addEventListener('change', this.onRangeChange.bind(this));
//       element.addEventListener('keydown', this.onKeyDown.bind(this));
//     });
//     this.setMinAndMaxValues();
//   }

//   onRangeChange(event) {
//     this.adjustToValidValues(event.currentTarget);
//     this.setMinAndMaxValues();
//   }

//   onKeyDown(event) {
//     if (event.metaKey) return;

//     const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
//     if (!event.key.match(pattern)) event.preventDefault();
//   }

//   setMinAndMaxValues() {
//     const inputs = this.querySelectorAll('input');
//     const minInput = inputs[0];
//     const maxInput = inputs[1];
//     if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
//     if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
//     if (minInput.value === '') maxInput.setAttribute('data-min', 0);
//     if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
//   }

//   adjustToValidValues(input) {
//     const value = Number(input.value);
//     const min = Number(input.getAttribute('data-min'));
//     const max = Number(input.getAttribute('data-max'));

//     if (value < min) input.value = min;
//     if (value > max) input.value = max;
//   }
// }

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);